import { Suspense, lazy } from 'react';
import type { ComponentType, LazyExoticComponent } from 'react';

import { createBrowserRouter } from 'react-router';
import type { RouteObject } from 'react-router';

import { GuestOnly, RequireSession, RouteGuard } from '@/app/guards';
import { CrashPage, NotFoundPage, PageSkeleton, PendingPage } from '@/app/pages/SystemPages';
import { RootLayout } from '@/app/RootLayout';
import type { RouteEntry } from '@/app/route-manifest';
import { ROUTES } from '@/app/route-manifest';
import { AppShell } from '@/app/shell/AppShell';
import { AuthLayout } from '@/app/shell/AuthLayout';
import { PublicLayout } from '@/app/shell/PublicLayout';

/**
 * Le routeur, construit depuis `route-manifest.ts`.
 *
 * Chaque page est chargée à la demande (un fichier par écran) : le premier
 * affichage ne télécharge que la page ouverte. Une page absente du disque
 * s'affiche en « écran en construction » plutôt que de casser.
 */
const pages = import.meta.glob<{ default: ComponentType }>('../features/*/pages/*Page.tsx');

const lazyCache = new Map<string, LazyExoticComponent<ComponentType>>();

function pageFor(entry: RouteEntry) {
  const key = `../features/${entry.feature}/pages/${entry.page}.tsx`;
  const loader = pages[key];
  if (!loader) {
    return function Missing() {
      return <PendingPage entry={entry} />;
    };
  }
  let component = lazyCache.get(key);
  if (!component) {
    component = lazy(loader);
    lazyCache.set(key, component);
  }
  return component;
}

function routeFor(entry: RouteEntry): RouteObject {
  const Page = pageFor(entry);
  return {
    path: entry.path,
    handle: { layout: entry.layout, entry },
    element: (
      <RouteGuard entry={entry}>
        <Suspense fallback={<PageSkeleton />}>
          <Page />
        </Suspense>
      </RouteGuard>
    ),
  };
}

export function createRouter() {
  const guest = ROUTES.filter((entry) => entry.access === 'guest');
  const privateRoutes = ROUTES.filter((entry) => entry.access === 'private');
  const publicRoutes = ROUTES.filter((entry) => entry.access === 'public');

  return createBrowserRouter([
    {
      element: <RootLayout />,
      errorElement: <CrashPage />,
      children: [
        {
          element: (
            <GuestOnly>
              <AuthLayout />
            </GuestOnly>
          ),
          children: guest.map(routeFor),
        },
        {
          element: <PublicLayout />,
          children: publicRoutes.map(routeFor),
        },
        {
          element: (
            <RequireSession>
              <AppShell />
            </RequireSession>
          ),
          children: privateRoutes.map(routeFor),
        },
        { path: '*', element: <NotFoundPage /> },
      ],
    },
  ]);
}
